import { useState, useMemo } from 'react'
import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import { Event } from '@/types/events'

// Extend dayjs with necessary plugins
dayjs.extend(isSameOrBefore)
dayjs.extend(isSameOrAfter)

// 0 = ongoing, 1 = upcoming, 2 = past. Dates are YYYY-MM-DD strings, so plain
// string comparison orders them correctly.
const timelineRank = (event: Event, today: string) => {
  if (event.end_date < today) return 2
  if (event.start_date > today) return 1
  return 0
}

// Default list order: ongoing and upcoming events by start date, then past
// events with the most recent first so the oldest event sits at the bottom.
// Clicking a column header still re-sorts the table.
const compareByTimeline = (today: string) => (a: Event, b: Event) => {
  const rankA = timelineRank(a, today)
  const rankDiff = rankA - timelineRank(b, today)
  if (rankDiff !== 0) return rankDiff
  if (rankA === 2) return b.end_date.localeCompare(a.end_date)
  return a.start_date.localeCompare(b.start_date)
}

export function useEventFilters(events: Event[]) {
  const [searchText, setSearchText] = useState<string>('')
  const [activeTab, setActiveTab] = useState<string>('all')
  const [selectedOwners, setSelectedOwners] = useState<string[]>([])
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [selectedDateRange, setSelectedDateRange] = useState<
    [dayjs.Dayjs | null, dayjs.Dayjs | null] | null
  >(null)
  const [ownerFilterVisible, setOwnerFilterVisible] = useState<boolean>(false)
  const [categoryFilterVisible, setCategoryFilterVisible] = useState<boolean>(false)
  const [dateFilterVisible, setDateFilterVisible] = useState<boolean>(false)

  const filteredEvents = useMemo(() => {
    const today = dayjs().format('YYYY-MM-DD')

    return events.filter((event) => {
      // Filter by search text
      const matchesSearch =
        event.name.toLowerCase().includes(searchText.toLowerCase()) ||
        (event.client_name && event.client_name.toLowerCase().includes(searchText.toLowerCase()))

      // Filter by status tab
      const matchesStatus = activeTab === 'all' || event.status === activeTab

      // Filter by selected owners
      const matchesOwners =
        selectedOwners.length === 0 ||
        (event.owner_ids &&
          event.owner_ids.some((id) => selectedOwners.includes(String(id)))) ||
        (event.owners &&
          event.owners.some((owner) => selectedOwners.includes(owner.id?.toString() || '')))

      // Filter by selected categories
      const matchesCategories =
        selectedCategories.length === 0 ||
        (event.category_ids && event.category_ids.some((id) => selectedCategories.includes(id))) ||
        (event.categories &&
          event.categories.some((category) => selectedCategories.includes(category.id || '')))

      // Filter by date range
      const matchesDateRange =
        !selectedDateRange ||
        (() => {
          const [startDate, endDate] = selectedDateRange
          if (!startDate || !endDate) return true

          const eventStartDate = dayjs(event.start_date)
          const eventEndDate = dayjs(event.end_date)

          // Check if event overlaps with selected date range
          return (
            eventStartDate.isSameOrBefore(endDate, 'day') &&
            eventEndDate.isSameOrAfter(startDate, 'day')
          )
        })()

      return matchesSearch && matchesStatus && matchesOwners && matchesCategories && matchesDateRange
    }).sort(compareByTimeline(today))
  }, [events, searchText, activeTab, selectedOwners, selectedCategories, selectedDateRange])

  const clearFilters = () => {
    setSearchText('')
    setActiveTab('all')
    setSelectedOwners([])
    setSelectedCategories([])
    setSelectedDateRange(null)
    setOwnerFilterVisible(false)
    setCategoryFilterVisible(false)
    setDateFilterVisible(false)
  }

  return {
    searchText,
    setSearchText,
    activeTab,
    setActiveTab,
    selectedOwners,
    setSelectedOwners,
    selectedCategories,
    setSelectedCategories,
    selectedDateRange,
    setSelectedDateRange,
    ownerFilterVisible,
    setOwnerFilterVisible,
    categoryFilterVisible,
    setCategoryFilterVisible,
    dateFilterVisible,
    setDateFilterVisible,
    filteredEvents,
    clearFilters,
  }
}
